/**
 * Handles the process of remembering a password by getting the user email address and sending
 * him/her an email.
 * The reason this is looks like the {@link RememberPasswordContainer} and it's part of the
 * `legacy` module is because it's intended for application that haven't implemented the SSO
 * yet, so they don't send a SSO Client configuration, therefore there's no SSO state from which
 * the container can deduce the return URL from. Instead of using the SSO state, this container
 * validates the HTTP referrer.
 */
class RememberPasswordLegacyContainer {
  /**
   * @param {ngQ}                   $q                To create an already rejected promise. This
   *                                                  is used when the request is made while
   *                                                  another is already being processed.
   * @param {Window}                $window           To get the referrer.
   * @param {FrontendConfiguration} appConfiguration  To get the Olapic's privacy policy URL.
   * @param {Legacy}                legacy            To make the request to reset the password.
   */
  constructor($q, $window, appConfiguration, legacy) {
    'ngInject';

    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the browser window.
     * @type {Window}
     */
    this.$window = $window;
    /**
     * A local reference for the `legacy` service.
     * @type {Legacy}
     */
    this.legacy = legacy;
    /**
     * The Olapic's privacy policy URL, to show on a link.
     * @type {string}
     */
    this.privacyPolicyUrl = appConfiguration.privacyPolicyURL;
    /**
     * To URL for the _"go back"_ link. When the container gets mounted, it will be completed
     * with the referrer URL.
     * @type {string}
     */
    this.goBackUrl = '';
    /**
     * Once the information is submitted and validated, in case of error, this property will be
     * used to store the message and send it to the child component.
     * @type {string}
     */
    this.error = '';
    /**
     * Whether or not the container is processing the form.
     * @type {boolean}
     */
    this.loading = false;
    /**
     * Whether or not the process finished.
     * @type {boolean}
     */
    this.success = false;
    /**
     * Once the container gets mounted, this will have the URL of the referrer. Different from
     * `goBackUrl`, which gets sent to the child component, the referrer original value doesn't
     * have a protcol, that's why it's stored on a different property.
     * @type {string}
     * @access protected
     * @ignore
     */
    this._refUrl = '';
    /**
     * An expression to validate that the referrer URL is from a valid Olapic application.
     * @type {RegExp}
     * @access protected
     * @ignore
     */
    this._referrerRegex = /\/\/([a-zA-Z0-9-\.]+\.(?:photorank\.me|(?:by\-)?olapic\.com))(?:\/|$)/ig;
  }
  /**
   * When the container gets mounted, it calls the method that validates the referrer.
   */
  $onInit() {
    this._validateReferrerURL();
  }
  /**
   * Processes the form. This is the callback sent to the child component.
   * @param {string} email The user email address.
   * @return {Promise<undefined,Error>}
   */
  rememberPassword(email) {
    let result;
    if (this.loading) {
      // Reject the promise if the container is already processing the form.
      result = this.$q.reject(new Error('The request is in progress'));
    } else {
      // Turn on the _"processing flag"_.
      this.loading = true;
      // Make the request to reset the password.
      result = this.legacy.rememberPassword(email, this._refUrl)
      .then(() => {
        // If everything went well, mark the `success` flag.
        this.success = true;
      })
      .catch((error) => {
        // If something went wrong, get the error message for the child component to show.
        this.error = error.message;
      })
      .then(() => {
        // Turn off the _"processing flag"_.
        this.loading = false;
      });
    }

    return result;
  }
  /**
   * This is called when the container gets mounted, and it's in charge of validating the referrer
   * (verifying that the URL is from a valid Olapic application domain) and saving the information
   * on the container properties.
   * @access protected
   * @ignore
   */
  _validateReferrerURL() {
    this._refUrl = '';
    const { referrer } = this.$window.document;
    if (referrer) {
      const matches = this._referrerRegex.exec(referrer);
      if (matches) {
        const [, url] = matches;
        this._refUrl = url;
        this.goBackUrl = `https://${url}`;
      }
    }
  }
}
/**
 * Shows and handles the logic of the _"remember your password"_ screen for applications that
 * haven't implemented SSO yet.
 * @ngesdoc component
 * @ngname rememberPasswordLegacyContainer
 * @ngcontroller {@link RememberPasswordLegacyContainer}
 * @ngcontainer
 * @ngmodule legacy
 */
export default {
  controller: RememberPasswordLegacyContainer,
  template: `
    <remember-password
      error="$ctrl.error"
      loading="$ctrl.loading"
      success="$ctrl.success"
      on-remember="$ctrl.rememberPassword(email)"
      go-back-url="$ctrl.goBackUrl"
      privacy-policy-url="$ctrl.privacyPolicyUrl"
    ></remember-password>
  `,
};
